<template>
  <div class="pr-2 pt-2 pb-2" :class="{ pointerNull: eventArchived }">
    <EventActionFieldRowComponent :label="t('components.TaskModal.title')">
      <InputText v-model="eventDescription" class="w-100" disabled />
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.TaskModal.user')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model="task.assigneeId"
        required
        :rules="[minValue(1)]"
        name="assignee"
      >
        <SearchSelect
          v-bind="inputField"
          v-model="task.assigneeId"
          data-testid="AbstractTaskModalAssignee"
          :disabled="disableAssignee || disabled"
          :site-permission-filter="siteId"
          :mode="SelectMode.USER"
          :filters="
            siteId == null
              ? undefined
              : {
                  hasRightsOnSite: {
                    rights: SQDC_ASSING_MINIMUM_RIGHTS,
                    siteIds: [siteId],
                  },
                }
          "
          :state="valid"
          :placeholder="t('components.TaskModal.userPlaceholder')"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.TaskModal.deadline')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        :model-value="task.deadline"
        required
        :rules="[validDate()]"
        name="deadline"
      >
        <DatePickerComponentNew
          v-bind="inputField"
          v-model="deadline"
          data-testid="AbstractTaskModalComponentDatePicker"
          :type="DatePickerType.DAY"
          class="w-100"
          :disabled="disableDate || disabled"
          :state-as-string="disableDate ? undefined : String(valid)"
          :placeholder="t('components.TaskModal.deadlinePlaceholder')"
          :tz="tz"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <slot name="description">
      <EventActionFieldRowComponent :label="t('components.TaskModal.description')">
        <InputRow
          v-slot="{ field: inputField, validation: { valid } }"
          v-model="task.description"
          required
          :rules="[minLength(1)]"
          name="description"
        >
          <PrimeTextArea
            v-bind="inputField"
            v-model="task.description"
            data-testid="AbstractTaskModalComponentTextarea"
            class="w-100"
            auto-resize
            :state="valid"
            :disabled="disabled"
            :placeholder="t('components.TaskModal.descriptionPlaceholder')"
            rows="4"
            @keydown.meta.enter="$emit('save', $event)"
            @keydown.ctrl.enter="$emit('save', $event)"
          />
        </InputRow>
      </EventActionFieldRowComponent>
    </slot>
    <slot name="beforeStatus" />

    <EventActionFieldRowComponent v-if="task.status" :label="t('components.TaskModal.status')">
      <GridRow>
        <div class="flex flex-wrap">
          <StatusSelectComponent
            v-if="task.status"
            v-model:status="task.status"
            :disabled="disabled"
            class="flex justify-content-start pt-2 pl-2"
          />
          <div class="pt-1">
            <FMFileInlineUploadPreview
              v-model:files-ids="task.fileIds"
              formatted-thumbnails
              removable-thumbnails
              :disabled="disabled"
            />
          </div>
        </div>
      </GridRow>
    </EventActionFieldRowComponent>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { minLength, minValue, validDate } from "@/modules/formValidation";
import InputRow from "@/components/shared/form/InputRow.vue";
import { computed, type PropType } from "vue";
import PrimeTextArea from "primevue/textarea";
import InputText from "primevue/inputtext";
import {
  EventType,
  useAuditNameQuery,
  type EventListEventFields_AuditResult_Fragment,
  type EventListEventFieldsFragment,
} from "@/graphql";
import GridRow from "@/components/shared/utility/GridRow.vue";
import StatusSelectComponent from "../StatusSelectComponent.vue";

import injectAsserted from "@/functions/assert/injectAsserted";
import DatePickerComponentNew from "@/components/shared/DatePickerComponentNew.vue";
import EventActionFieldRowComponent from "../../EventActionFieldRowComponent.vue";
import { TimeZoneInjectionKey } from "@/functions/timezoneUtil";
import { DatePickerType } from "@/components/shared/DatePickerComponentHelper";
import type { EditTaskType } from "../helper/DefaultTask";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { useTranslation } from "@/language/translation";
import apolloCache from "@/graphql/apolloCache";
import { gql } from "@apollo/client";
import { DateTime } from "luxon";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { SQDC_ASSING_MINIMUM_RIGHTS } from "../helper/SQDCHelper";
import FMFileInlineUploadPreview from "@/modules/filemanagement/components/FMFileInlineUploadPreview.vue";
import type { EventEditType } from "../../event/modal/EventEditType";

const props = defineProps({
  task: {
    type: Object as PropType<EditTaskType>,
    required: true,
  },
  event: {
    type: Object as PropType<EventListEventFieldsFragment | EventEditType>,
    required: true,
  },
  disableDate: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableAssignee: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
});
defineEmits(["save"]);
const tz = injectAsserted(TimeZoneInjectionKey);

const { t, getDisplayName } = useTranslation();

const deadline = computed({
  get: () => {
    return props.task.deadline == null ? undefined : DateTime.fromISO(props.task.deadline);
  },
  set: (val: DateTime | null) => {
    props.task.deadline = val?.toISO();
  },
});

const useNameProps = computed(() => {
  if (
    !("auditId" in props.event) ||
    props.event?.auditId == null ||
    !("auditRevision" in props.event) ||
    props.event?.auditRevision == null
  ) {
    return undefined;
  }
  return {
    id: props.event.auditId! as number,
    revision: props.event.auditRevision as number,
  };
});

const { loading, result } = useAuditNameQuery(
  () => ({
    id: useNameProps.value!.id,
    revision: useNameProps.value!.revision,
  }),
  computed(() => ({
    fetchPolicy: "cache-first",
    enabled: useNameProps.value != null && props.event.__typename === EventType.AuditResult,
  })),
);

const auditName = computed(() => {
  if (loading.value) {
    return t("global.loading");
  }
  const audit = result.value?.audits?.[0];
  return audit ? getDisplayName(audit) : null;
});
export type TaskWithTaskAuditResultConnection =
  EventListEventFields_AuditResult_Fragment["actions"][number] & {
    __typename: "Task";
  };
function getAuditQuestionFromCache(
  auditResult: EventListEventFields_AuditResult_Fragment,
  task: TaskWithTaskAuditResultConnection,
): null | string {
  if (task.TaskAuditResultConnection?.questionId == null) {
    return auditName.value ?? auditResult.location;
  }

  const identifiedAuditQuestion = apolloCache.identify({
    __typename: "AuditQuestion",
    id: task.TaskAuditResultConnection?.questionId,
  });
  return (
    apolloCache.readFragment<{ id: number; text: string }>({
      id: identifiedAuditQuestion,
      fragment: gql`
        fragment AuditQuestionFrag on AuditQuestion {
          id
          text
        }
      `,
    })?.text ??
    auditName.value ??
    auditResult.location
  );
}

const eventDescription = computed(() => {
  switch (props.event.__typename) {
    case EventType.AuditResult: {
      return getAuditQuestionFromCache(props.event as any, props.task as any) ?? t("global.na");
    }
    case EventType.Issue:
      return (props.event as any).description;
    case EventType.IHFailureNotice:
      return (props.event as any).description;
    case EventType.IHMaintenance:
      return (props.event as any).description;
    case EventType.HLPIdea:
      return (props.event as any).ideaIs;
    case EventType.QualityAssurance:
      return (props.event as any).description;
    default:
      throw Error("unknown event type AbstractTaskModal");
  }
});

const eventArchived = computed(() => props.event.deletedAt != null);

const { siteId } = useProvidedBoardContext();
</script>
